// TODAY'S VERSION (v1), kept beside the v1.1 one until the press (lib/release/next).
// Copied from main as it was on 2026-10-23; everyone who is not a test account sees this one.
// connect/setup.ts — ONE PASTE. The page an ai reads to connect itself.
//
// John, 2026-10-21: copying a link into Claude or ChatGPT, following a guide
// and waiting for innernet to hear the first call was too much. The agent
// memory module already showed the way (#295): hand the person a few lines to
// paste into their ai, and a link that teaches that ai the rest. This is the
// same move for the memory itself.
//
// The person pastes three lines into ANY ai. The ai fetches /connect/ai (this
// module, as markdown) or /connect/ai.json (the same facts as a schema), finds
// itself in the table, and then either does it (a coding agent can run a
// command or edit its own config) or walks the person through it one step at a
// time (a chat app cannot add its own connectors). It proves the connection by
// calling innernet, which is also what lights the connection up on the
// person's connections page: an untagged first call ARRIVES as a connection of
// its own (app/api/mcp/route.ts touchOrAdoptByClient), so nothing has to be
// minted before the paste.
//
// Everything a tool needs comes from CONNECT_TOOLS (the recorded log the
// connect window walks), so the two never disagree. The only facts added here
// are the ones a model needs and a person does not: which tools can wire
// themselves, and the exact command or file that does it.
//
// Pure: no I/O, importable by the route, the panel and tests alike.

import { CONNECT_TOOLS, MCP_URL, PROMPT_SYNC, type ConnectTool } from './tools'
import { connectionUrl } from './connections'

/** where the guide lives, for the paste and for anything that links to it */
export const SETUP_URL = 'https://innernet.live/connect/ai'
export const SETUP_JSON_URL = 'https://innernet.live/connect/ai.json'
export const EXTENSION_URL = 'https://innernet.live/connect/extension'
export const SETUP_SCHEMA = 'innernet.connect/1'
export const SETUP_VERSION = '1.0.0'
export const SETUP_UPDATED = '2026-10-21'

/** a connection's own tag, as minted (lib/connect/connections mintToken) */
const DOOR_TAG = /^[0-9a-f]{32}$/

/** The server url the guide hands out: a connection's own door when a valid tag
 *  rides in, the shared door otherwise. Anything else is ignored, never echoed. */
export function setupEndpoint(door?: string | null): string {
  const tag = (door ?? '').trim().toLowerCase()
  return DOOR_TAG.test(tag) ? connectionUrl(tag) : MCP_URL
}

// ── the lines a person pastes ─────────────────────────────────────────────────

/** The paste. Three lines a person drops into any ai: what it is, where the
 *  steps are, and the link itself, so an ai that cannot open links still has
 *  the one thing it must hand over. With a door, the guide carries it too. */
export function pasteLines(endpoint: string = MCP_URL): string[] {
  const tag = endpoint.match(/\/api\/mcp\/c\/([0-9a-f]{32})$/)?.[1]
  return [
    'connect innernet, my memory, to this ai.',
    `read ${SETUP_URL}${tag ? `?door=${tag}` : ''} and follow it: set it up yourself if you can, or walk me through it one step at a time.`,
    `the link is ${endpoint}`,
  ]
}

export function pasteText(endpoint: string = MCP_URL): string {
  return pasteLines(endpoint).join('\n')
}

// ── what a model needs that a person does not ────────────────────────────────

/** How a coding agent wires ITSELF: a command it can run, or a file it can
 *  write. `{url}` is replaced by the server url. Only tools whose own agent can
 *  reach its own config are here; every other tool is walked by hand. */
interface SelfWire {
  /** commands, run in order */
  run?: string[]
  /** a config file the agent merges the tool's block into */
  file?: string
  /** how the person finishes the sign-in once it is wired */
  signIn: string
}

const SELF: Record<string, SelfWire> = {
  'claude-code': {
    run: ['claude mcp add --transport http --scope user innernet {url}'],
    signIn: 'in Claude Code, type /mcp, choose innernet and sign in with the browser. new tools load in a new session.',
  },
  codex: {
    run: ['codex mcp add innernet --url {url}', 'codex mcp login innernet'],
    signIn: 'the login command opens the browser to sign in. restart codex so the tools load.',
  },
  'gemini-cli': {
    run: ['gemini mcp add --transport http --scope user innernet {url}'],
    signIn: 'restart gemini, run /mcp auth innernet and sign in with the browser.',
  },
  vscode: {
    run: [`code --add-mcp '{"name":"innernet","type":"http","url":"{url}"}'`],
    signIn: 'VS Code asks to sign in the first time a tool runs. say yes and sign in with the browser.',
  },
  cursor: {
    file: '~/.cursor/mcp.json',
    signIn: 'in Cursor, open Settings → Tools & MCP, find innernet and press the sign in button beside it.',
  },
  windsurf: {
    file: '~/.codeium/windsurf/mcp_config.json',
    signIn: 'in Cascade, refresh the MCP list and sign in with the browser when it asks.',
  },
  antigravity: {
    file: '~/.gemini/config/mcp_config.json',
    signIn: 'in the agent panel, open … → MCP Servers, refresh, and sign in with the browser when it asks.',
  },
  continue: {
    file: '~/.continue/config.yaml',
    signIn: 'reload Continue and sign in with the browser when it asks.',
  },
}

/** the apps that cannot hold a remote MCP server today, and what to do instead */
export const NO_MCP_APPS = ['the Gemini app', 'Grok', 'Perplexity', 'Meta AI', 'Microsoft Copilot', 'DeepSeek'] as const

// ── the schema ───────────────────────────────────────────────────────────────

export interface SetupTool {
  id: string
  name: string
  /** chat: a person adds it in the app's settings. agent: it can wire itself. */
  kind: 'chat' | 'agent'
  /** what the person or the agent puts in: a link, a block, or a command */
  paste: ConnectTool['paste']
  /** exactly where it goes */
  where: string | null
  /** the thing to put there, with the server url inside */
  value: string
  /** the person's steps, in order */
  steps: string[]
  /** present when the agent can do it itself */
  self: null | { run: string[]; file: string | null; block: string | null; sign_in: string }
  /** a one-press install link, when the tool has one */
  deeplink: string | null
  /** stopping it from asking before every call */
  trust: string[]
  /** where the standing instruction goes */
  instructions_where: string
}

export interface SetupSchema {
  schema: typeof SETUP_SCHEMA
  version: string
  updated: string
  guide: string
  server: {
    name: 'innernet'
    url: string
    transport: 'streamable-http'
    auth: 'oauth'
    note: string
  }
  tools: SetupTool[]
  verify: { call: string; then: string; ask_in_a_new_chat: string }
  standing_instructions: string
  no_mcp: { apps: readonly string[]; instead: string[] }
  never: string[]
}

// the recorded url first, then the placeholder: a door url starts with MCP_URL,
// so the other order would swap the door's own prefix a second time
const fill = (s: string, url: string) => s.split(MCP_URL).join(url).split('{url}').join(url)

export function setupTool(t: ConnectTool, url: string): SetupTool {
  const self = SELF[t.id]
  const value = fill(t.config, url)
  return {
    id: t.id,
    name: t.generic ? 'any other MCP client' : t.name,
    kind: self ? 'agent' : 'chat',
    paste: t.paste,
    where: t.where ?? null,
    value,
    steps: t.steps.map((s) => fill(s, url)),
    self: self
      ? {
          run: (self.run ?? []).map((c) => fill(c, url)),
          file: self.file ?? null,
          block: self.file ? value : null,
          sign_in: self.signIn,
        }
      : null,
    deeplink: t.deeplink ? t.deeplink(url) : null,
    trust: t.trust ?? [],
    instructions_where: t.promptWhere,
  }
}

const VERIFY_ASK = 'innernet, what do you know about me?'

export function setupSchema(endpoint: string = MCP_URL): SetupSchema {
  // the tools that wire themselves first, then the chat apps, then the open door
  const tools = CONNECT_TOOLS.map((t) => setupTool(t, endpoint))
  const rank = (t: SetupTool) => (t.id === 'other' ? 2 : t.kind === 'agent' ? 0 : 1)
  tools.sort((a, b) => rank(a) - rank(b))
  return {
    schema: SETUP_SCHEMA,
    version: SETUP_VERSION,
    updated: SETUP_UPDATED,
    guide: SETUP_URL,
    server: {
      name: 'innernet',
      url: endpoint,
      transport: 'streamable-http',
      auth: 'oauth',
      note: 'a remote MCP server. it signs in with the person\'s browser the first time; there is no key or password to paste.',
    },
    tools,
    verify: {
      call: 'innernet_list_projects',
      then: 'tell the person, in a line, what you can see: their projects by name, or that the memory is new and empty.',
      ask_in_a_new_chat: VERIFY_ASK,
    },
    standing_instructions: PROMPT_SYNC,
    no_mcp: {
      apps: NO_MCP_APPS,
      instead: [
        `the innernet browser extension carries the memory into the chat sites it knows (ChatGPT, Claude, Gemini) from Chrome, Edge, Brave, Arc, Opera, Vivaldi, Firefox and Safari: ${EXTENSION_URL}`,
        'or connect a coding agent on the same machine (Claude Code, Codex, Gemini CLI, Cursor), which reads the same memory',
      ],
    },
    never: [
      'never ask for a password, a code from an email, or an api key. the sign-in happens in their browser, on innernet.live.',
      'never remove or rewrite other servers in a config file. add innernet beside them.',
      'never press a confirm or allow button for them in a settings page. they press it.',
      'never invent a menu that is not there. if a step does not match what they see, ask what they see and adapt.',
    ],
  }
}

// ── the guide, as a model reads it ───────────────────────────────────────────

function toolSection(t: SetupTool): string {
  const out: string[] = [`### ${t.name}`]
  if (t.self) {
    out.push('you can do this yourself.')
    if (t.self.run.length) {
      out.push('run:', '```', ...t.self.run, '```')
    }
    if (t.self.file && t.self.block) {
      out.push(`add innernet to \`${t.self.file}\` (merge it under the existing key; create the file if it is not there):`, '```', t.self.block, '```')
    }
    out.push(`then: ${t.self.sign_in}`)
    out.push(`if you cannot, give them these steps instead: ${t.steps.join(' · ')}`)
    // the by-hand steps of a block tool say "the block below": it has to be there
    if (t.paste === 'block' && !t.self.file) out.push('the block:', '```', t.value, '```')
    if (t.paste === 'command') out.push('the command:', '```', t.value, '```')
  } else {
    out.push('walk them through it. one step per message, wait for "done" before the next.')
    out.push(...t.steps.map((s, i) => `${i + 1}. ${s}`))
    if (t.where) out.push(`it goes in: ${t.where}`)
    out.push(t.paste === 'link' ? `the link they paste: ${t.value}` : ['what they paste, whole:', '```', t.value, '```'].join('\n'))
  }
  if (t.deeplink) out.push(`one press, if they are on that machine: ${t.deeplink}`)
  if (t.trust.length) out.push(`so it stops asking every time: ${t.trust.join(', then ')}.`)
  out.push(`the standing instruction goes in: ${t.instructions_where}.`)
  return out.join('\n')
}

/** The guide as markdown, manifest first (the skill class: the manifest tells a
 *  reader when to use it before it reads a line). */
export function setupMarkdown(endpoint: string = MCP_URL): string {
  const s = setupSchema(endpoint)
  const agents = s.tools.filter((t) => t.kind === 'agent')
  const chats = s.tools.filter((t) => t.kind === 'chat')
  return [
    '---',
    'name: connect-innernet',
    'title: connect innernet to this ai',
    'kind: skill',
    `version: ${s.version}`,
    'description: >',
    '  connect the person\'s innernet memory to the ai reading this, over MCP. a coding agent',
    '  wires itself in with one command or one config entry; a chat app is walked through its',
    '  own settings one step at a time. then prove it with one call.',
    'when_to_load: >',
    '  when someone pastes this link and asks you to connect innernet, their memory, to you.',
    'authored_by: innernet',
    'author_url: https://innernet.live',
    `updated: ${s.updated}`,
    `schema: ${SETUP_JSON_URL}`,
    '---',
    '',
    '# connect innernet to this ai',
    '',
    'you are helping someone connect innernet to the ai they are talking to, which is you. innernet is their one memory across every ai tool (innernet.live): their projects, their decisions, what they like and how they work. it is a remote MCP server, and it signs in with their browser the first time, so there is no key and no password anywhere in this.',
    '',
    `**the server:** \`${s.server.url}\``,
    '',
    'if the message they pasted carries a link of its own, use that one: it is the same server, and it lets their connections page name this connection the moment it calls.',
    '',
    '## first, find yourself',
    '',
    'you know which app or agent you are. find it below. if you truly cannot tell, ask one question: "which app is this, and on which device?". keep the rest of the conversation short: one step at a time, in plain words, no lectures.',
    '',
    '- **you can run commands or edit files** (Claude Code, Codex, Gemini CLI, VS Code with Copilot, Cursor, Windsurf, Antigravity, Continue): do it yourself, under **you can do it yourself**.',
    '- **you are a chat app** (Claude on the web, the desktop or the phone, ChatGPT, Notion, Zed, Cline, anything else): you cannot add a connector for them. walk them through their own settings, under **walk them through it**.',
    `- **this app cannot hold an MCP server** (${s.no_mcp.apps.join(', ')}): say so plainly, in one line, and offer what is under **when there is no place for it**.`,
    '',
    'on a phone, a connector is added on the web (claude.ai, chatgpt.com) once, and the phone app has it from then on.',
    '',
    '## you can do it yourself',
    '',
    'tell them in one line what you are about to do, then do it. the command or the file below wires only you. their browser opens to sign in to innernet; they sign in there (or make an account), and come back.',
    '',
    'if they would like every ai on this machine connected at once, `npx -y innernet connect` signs in once and wires all of them (and asks before it connects the folder you are in to a memory). it waits for the browser sign-in, so run it where they can see it.',
    '',
    ...agents.map(toolSection).flatMap((x) => [x, '']),
    '## walk them through it',
    '',
    'you cannot open their settings, so be their eyes: one step per message, the exact words of the button to press, and the link exactly as given. when a step does not match what they see (apps move their menus), ask what they see and adapt; never invent a menu.',
    '',
    'if their app offers to add a connector from its own directory right here in the chat, and innernet is listed there, offer that first: it is the shortest way.',
    '',
    ...chats.map(toolSection).flatMap((x) => [x, '']),
    '## prove it works',
    '',
    `as soon as innernet's tools are in your list, call \`${s.verify.call}\`, then ${s.verify.then} that first call is also what makes the connection show up as live on their connections page.`,
    '',
    `a chat app usually loads a new connector in a new chat. if the tools are not in your list yet, ask them to open a new chat here and say: "${s.verify.ask_in_a_new_chat}"`,
    '',
    '## so it keeps working',
    '',
    'innernet is most useful when the ai reaches for it on its own. offer, once, to keep this standing instruction where the tool reads one (each tool above says where).',
    '',
    '- a coding agent: offer to add it to the file the tool reads (AGENTS.md, CLAUDE.md, GEMINI.md, the rules file). ask before you write.',
    '- a chat app: give them the text to paste into the place named above.',
    '',
    '```',
    s.standing_instructions,
    '```',
    '',
    '## when there is no place for it',
    '',
    ...s.no_mcp.instead.map((l) => `- ${l}`),
    '',
    '## never',
    '',
    ...s.never.map((l) => `- ${l}`),
    '',
    `the same facts, as a schema: ${SETUP_JSON_URL}`,
    '',
  ].join('\n')
}

/** can this tool's own agent wire it in (a command, its own config file)? */
export const wiresItself = (id: string) => !!SELF[id]

// ── hearing it arrive ────────────────────────────────────────────────────────

/** What the panel knew at the moment of the copy: each connection and whether
 *  it had ever called. Compared by id, never by clock, so a laptop clock that
 *  runs ahead of the server cannot hide an arrival. */
export type ArrivalBaseline = ReadonlyMap<string, boolean>

export function arrivalBaseline(conns: readonly { id: string; first_seen_at: string | null }[]): ArrivalBaseline {
  return new Map(conns.map((c) => [c.id, !!c.first_seen_at]))
}

/** The connection that made its FIRST call since the baseline: a new row the
 *  first call minted (an untagged arrival), or a minted door that was waiting.
 *  The newest one wins; null while nothing has. */
export function arrivedSince<C extends { id: string; first_seen_at: string | null }>(
  baseline: ArrivalBaseline,
  conns: readonly C[],
): C | null {
  const fresh = conns.filter((c) => !!c.first_seen_at && baseline.get(c.id) !== true)
  if (!fresh.length) return null
  return fresh.reduce((a, b) => (Date.parse(b.first_seen_at!) > Date.parse(a.first_seen_at!) ? b : a))
}
